import * as tslib_1 from "tslib";
/** @jsx jsx */

import { jsx } from "@emotion/core";
import * as React from "react";
import { Text } from "./Text";
import PropTypes from "prop-types";
import { RequestCloseContext } from "./Sheet";
import { useTheme } from "./Theme/Providers";
import { noOp } from "./misc/noop";
var KeyCodes = {
  ArrowUp: 38,
  ArrowDown: 40,
  Home: 36,
  End: 35
};
export var MenuList = function MenuList(_a) {
  var children = _a.children,
      _b = _a.focusableChildren,
      focusableChildren = _b === void 0 ? [] : _b,
      other = tslib_1.__rest(_a, ["children", "focusableChildren"]);

  var theme = useTheme();
  var disabled = new Map();

  var _c = React.useState(null),
      focusIndex = _c[0],
      setFocusIndex = _c[1];

  var kids = React.Children.toArray(children);
  var focusComponents = [MenuItem].concat(focusableChildren);
  var focusableItems = kids.filter(function (kid) {
    return React.isValidElement(kid) && focusComponents.indexOf(kid.type) > -1;
  });
  var lastIndex = focusableItems.length - 1;
  var firstIndex = 0;
  return jsx("div", tslib_1.__assign({
    role: "menu",
    tabIndex: 0,
    onKeyDown: function onKeyDown(e) {
      if (e.keyCode === KeyCodes.ArrowDown || e.keyCode === KeyCodes.ArrowUp && focusIndex === null) {
        e.preventDefault();
        setFocusIndex(0);
      }
    },
    css: {
      outline: "none",
      minWidth: "200px",
      display: "block",
      padding: theme.spaces.sm + " 0"
    }
  }, other), kids.map(function (kid) {
    if (!React.isValidElement(kid)) {
      return null;
    }

    var i = focusableItems.indexOf(kid);

    if (i < 0) {
      return kid;
    }

    var k = kid;
    disabled.set(i, k.props.disabled);

    function focusDown(current) {
      var next = current + 1 > lastIndex ? firstIndex : current + 1;

      if (disabled.get(next)) {
        focusDown(next);
      } else {
        setFocusIndex(next);
      }
    }

    function focusUp(current) {
      var next = current - 1 < firstIndex ? lastIndex : current - 1;

      if (disabled.get(next)) {
        focusUp(next);
      } else {
        setFocusIndex(next);
      }
    }

    var index = focusIndex || 0;
    return React.cloneElement(k, {
      focus: i === focusIndex,
      onFocus: function onFocus() {
        if (i !== focusIndex) {
          setFocusIndex(i);
        }
      },
      onKeyDown: function onKeyDown(e) {
        if (e.keyCode === KeyCodes.ArrowDown) {
          e.preventDefault();
          focusDown(index);
        } else if (e.keyCode === KeyCodes.ArrowUp) {
          e.preventDefault();
          focusUp(index);
        } else if (e.keyCode === KeyCodes.Home) {
          e.preventDefault();
          setFocusIndex(firstIndex);
        } else if (e.keyCode === KeyCodes.End) {
          e.preventDefault();
          setFocusIndex(lastIndex);
        }
      }
    });
  }));
};
MenuList.propTypes = {
  children: PropTypes.node,
  focusableChildren: PropTypes.arrayOf(PropTypes.elementType)
};
export var MenuItem = function MenuItem(_a) {
  var focus = _a.focus,
      onFocus = _a.onFocus,
      _onKeyDown = _a.onKeyDown,
      onSelect = _a.onSelect,
      contentBefore = _a.contentBefore,
      contentAfter = _a.contentAfter,
      _onClick = _a.onClick,
      _b = _a.component,
      Component = _b === void 0 ? "div" : _b,
      _c = _a.role,
      role = _c === void 0 ? "menuitem" : _c,
      children = _a.children,
      disabled = _a.disabled,
      other = tslib_1.__rest(_a, ["focus", "onFocus", "onKeyDown", "onSelect", "contentBefore", "contentAfter", "onClick", "component", "role", "children", "disabled"]);

  var _d;

  var theme = useTheme();
  var dark = theme.colors.mode === "dark";
  var localRef = React.useRef(null);
  var closeParent = React.useContext(RequestCloseContext);
  React.useEffect(function () {
    if (focus && localRef.current) {
      localRef.current.focus();
    }
  }, [focus, localRef]);
  var isLink = Component === "a" || other.href || other.to;

  function select() {
    if (onSelect) {
      onSelect();
    }

    closeParent();
  }

  return jsx(Component, tslib_1.__assign({
    onTouchStart: noOp,
    css: (_d = {
      cursor: "pointer",
      padding: "calc(" + theme.spaces.sm + " + 0.25rem) calc(" + theme.spaces.md + " + 0.25rem)"
    }, _d[theme.mediaQueries.sm] = {
      padding: theme.spaces.sm + " " + theme.spaces.md
    }, _d.opacity = disabled ? 0.3 : 1, _d.display = "flex", _d.textDecoration = "none", _d.transition = "background-color 0.1s ease", _d.WebkitTapHighlightColor = "transparent", _d.color = theme.colors.text.default, _d.alignItems = "center", _d.pointerEvents = disabled ? "none" : "initial", _d["@media (hover: hover)"] = {
      ":hover": {
        background: dark ? theme.colors.background.tint2 : theme.colors.background.tint1
      }
    }, _d[":focus"] = {
      backgroundColor: dark ? theme.colors.background.tint2 : theme.colors.background.tint1,
      outline: "none"
    }, _d[":active"] = {
      backgroundColor: theme.colors.background.tint2
    }, _d[theme.mediaQueries.md] = {
      padding: theme.spaces.xs + " " + theme.spaces.md
    }, _d),
    role: role,
    tabIndex: 0,
    "data-trigger-close": true,
    onClick: function onClick(e) {
      if (_onClick) _onClick(e);
      select();
    },
    onKeyDown: function onKeyDown(e) {
      e.stopPropagation();
      if (_onKeyDown) _onKeyDown(e);

      if (e.key === "Enter") {
        if (!isLink) {
          e.preventDefault();
        }

        select();
      }
    },
    ref: localRef
  }, other), contentBefore, typeof children === "string" ? jsx(Text, {
    wrap: false,
    css: {
      paddingLeft: contentBefore ? theme.spaces.md : 0,
      paddingRight: contentAfter ? theme.spaces.md : 0,
      flex: 1,
      color: "inherit"
    }
  }, children) : children, typeof contentAfter === "string" ? jsx(Text, {
    muted: true
  }, contentAfter) : contentAfter);
};
MenuItem.propTypes = {
  onSelect: PropTypes.func,
  component: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node
};
export function MenuDivider(props) {
  var theme = useTheme();
  return jsx("div", tslib_1.__assign({
    css: {
      height: 0,
      margin: theme.spaces.sm + " 0",
      overflow: "hidden",
      borderTop: "1px solid",
      borderColor: theme.colors.border.muted
    }
  }, props));
}
export var MenuLabel = function MenuLabel(props) {
  var theme = useTheme();
  return jsx(Text, tslib_1.__assign({
    variant: "uppercase",
    css: {
      padding: theme.spaces.xs + " " + theme.spaces.md
    }
  }, props));
};
MenuLabel.propTypes = {
  children: PropTypes.node
};